import { NodeUtils } from 'Editor/services/DataManager';
import { StylesUtils } from './StylesUtils';
import { JsonRange } from 'Editor/services/_Common/Selection';
import { EditorDOMElements } from 'Editor/services/_Common/DOM';
import ReduxInterface from 'Editor/services/ReduxInterface';
import { Logger } from '_common/services';

type Timeouts = {
  [index: string]: NodeJS.Timeout;
};

export class ContentStylesHandler {
  private editionContext: Editor.Edition.Context;
  private stylesContext: Editor.Edition.Styles.Context;

  protected stylesToApply: Editor.Edition.InlineStylesMap = {};
  protected stylesToRemove: Editor.Edition.AggregatedStylesMap = {};

  private timeouts: Timeouts = {};

  constructor(
    editionContext: Editor.Edition.Context,
    stylesContext: Editor.Edition.Styles.Context,
  ) {
    this.editionContext = editionContext;
    this.stylesContext = stylesContext;
  }

  initialize() {}

  destroy() {}

  updateStyleToRedux(styles: Editor.Edition.StylesMap) {
    if (this.timeouts.updateStyleValues) {
      clearTimeout(this.timeouts.updateStyleValues);
    }

    this.timeouts.updateStyleValues = setTimeout(() => {
      ReduxInterface.updateToolbarValues(styles);
    }, 50);
  }

  resetPendingStyles() {
    this.stylesToApply = {};
    this.stylesToRemove = {};
  }

  havePendingStyles() {
    return this.havePendingStylesToApply() || this.havePendingStylesToRemove();
  }

  havePendingStylesToApply() {
    return Object.keys(this.stylesToApply).length > 0;
  }

  havePendingStylesToRemove() {
    return Object.keys(this.stylesToRemove).length > 0;
  }

  addStyleToApply<T extends Editor.Edition.Styles>(
    style: T,
    value: Exclude<Editor.Edition.StylesMap[T], undefined>,
  ) {
    if (!StylesUtils.isInlineStyle(style)) {
      return false;
    }

    let exclusiveStyle: Editor.Edition.InlineStyles | undefined =
      StylesUtils.INLINE_EXCLUSIVE_STYLES[style];

    if (StylesUtils.isInlineBooleanStyle(style) && this.stylesToApply[style] != null) {
      if (this.stylesToApply[style] != null) {
        // already exists
        if (this.stylesToApply[style] !== value) {
          // oposite value, just remove
          delete this.stylesToApply[style];
        }
      }
    } else if (exclusiveStyle != null && this.stylesToApply[exclusiveStyle] != null) {
      delete this.stylesToApply[exclusiveStyle];
      //@ts-expect-error remove this in the future when typescript-eslint is updated
      this.stylesToApply[style] = value;
    } else {
      //@ts-expect-error remove this in the future when typescript-eslint is updated
      this.stylesToApply[style] = value;
    }

    this.updateStyleToRedux({
      [style]: value,
    });

    return true;
  }

  async applyPendingStyles(
    range?: Editor.Selection.JsonRange,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    if (!this.editionContext.commandFactory) {
      return;
    }

    if (this.havePendingStylesToRemove()) {
      const command = this.editionContext.commandFactory.getCommand(
        'REMOVE_STYLES_COMMAND',
        this.stylesToRemove,
        range,
        options,
      );
      if (command) {
        await command.exec();
      }

      this.stylesToRemove = {};
    }

    if (this.havePendingStylesToApply()) {
      // exec apply styles command
      const command = this.editionContext.commandFactory.getCommand(
        'APPLY_STYLES_COMMAND',
        this.stylesToApply,
        range,
        options,
      );
      if (command) {
        await command.exec();
      }

      this.stylesToApply = {};
    }
  }

  async toggleStyleValue(...[style, value]: Editor.Edition.Styles.ToggleStylesArgs) {
    try {
      if (this.stylesContext.debug) {
        logger.info('StylesManager toggleStyleValue', style, value);
      }

      if (!this.editionContext.DataManager) {
        return;
      }

      if (!this.editionContext.untrackedActionWarning()) {
        return;
      }

      // restore selection to the browser
      this.editionContext.DataManager.selection.restore();

      const rangeData = this.editionContext.DataManager.selection.current;
      const jsonRange = JsonRange.buildFromRangeData(rangeData[0]);

      if (value != null) {
        this.addStyleToApply(style, value);
      }

      if (jsonRange.collapsed) {
        // collapsed selection

        const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(jsonRange.start.b);
        const baseData = baseModel?.selectedData();

        if (baseData) {
          const closestTableData = NodeUtils.closestOfTypeByPath(baseData, jsonRange.start.p, [
            'tbl',
          ]);

          if (closestTableData && closestTableData.data.id) {
            const tableElement = document.getElementById(closestTableData.data.id);
            if (EditorDOMElements.isTableElement(tableElement)) {
              const selectedCells = tableElement.getSelectedCells();

              if (selectedCells.length > 1) {
                // apply styles if multiple cells are selected
                await this.applyPendingStyles(undefined, { useSelectedCells: true });
              }
            }
          }
        }
        // TODO aply styles directly to non-editable block elements?
      } else {
        // non-collapsed selection
        await this.applyPendingStyles();
      }
    } catch (error) {
      Logger.warn(error);
    }
  }

  async removeStyleValue(...[style, value]: Editor.Edition.Styles.RemoveStylesArgs) {
    try {
      if (this.stylesContext.debug) {
        logger.info('StylesManager removeStyleValue', style, value);
      }

      if (!this.editionContext.DataManager) {
        return;
      }

      if (!StylesUtils.isInlineStyle(style)) {
        return;
      }

      if (!this.editionContext.untrackedActionWarning()) {
        return;
      }

      // restore selection to the browser
      this.editionContext.DataManager.selection.restore();

      const rangeData = this.editionContext.DataManager.selection.current;
      const jsonRange = JsonRange.buildFromRangeData(rangeData[0]);

      //@ts-expect-error
      this.stylesToRemove[style] = value != null ? [value] : null;

      if (jsonRange.collapsed) {
        // collapsed selection

        const baseModel = this.editionContext.DataManager.nodes.getNodeModelById(jsonRange.start.b);
        const baseData = baseModel?.selectedData();

        if (baseData) {
          const closestTableData = NodeUtils.closestOfTypeByPath(baseData, jsonRange.start.p, [
            'tbl',
          ]);

          if (closestTableData && closestTableData.data.id) {
            const tableElement = document.getElementById(closestTableData.data.id);
            if (EditorDOMElements.isTableElement(tableElement)) {
              const selectedCells = tableElement.getSelectedCells();

              if (selectedCells.length > 1) {
                // apply styles if multiple cells are selected
                await this.applyPendingStyles(undefined, { useSelectedCells: true });
              }
            }
          }
        }
        // TODO aply styles directly to non-editable block elements?
      } else {
        // non-collapsed selection
        await this.applyPendingStyles();
      }
    } catch (error) {
      Logger.warn(error);
    }
  }

  async clearFormat(
    rangeToClear?: Editor.Selection.JsonRange,
    options: Editor.Edition.ClearFormatCommandOptions = {},
  ) {
    if (this.stylesContext.debug) {
      logger.info('StylesManager clearFormat', options);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    const command = this.editionContext.commandFactory.getCommand(
      'CLEAR_FORMAT_COMMAND',
      rangeToClear,
      options,
    );

    if (command) {
      await command.exec();
    }
  }
}
